import { useState, useEffect, useCallback, useMemo } from 'react';
import { clientService } from 'api/services/clientService';
import type {
  ID, PaginatedResponse, Client,
  CreateClientRequest, UpdateClientRequest, ClientSearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0,
  hasNextPage: false, hasPreviousPage: false,
};

export const useClients = (params?: ClientSearchRequest) => {
  const [data, setData] = useState<Client[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<Client>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchClients = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await clientService.getAll(params);
      setData(r.data); setPagination(r.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  return { data, pagination, loading, error, refetch: fetchClients };
};

export const useClient = (id: ID | null | undefined) => {
  const [data, setData] = useState<Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchClient = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true); setError(null);
    try { const r = await clientService.getById(id); setData(r.data); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { fetchClient(); }, [fetchClient]);
  return { data, loading, error, refetch: fetchClient };
};

export const useCreateClient = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const createClient = async (data: CreateClientRequest, options?: { onSuccess?: (item: Client) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await clientService.create(data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { createClient, loading, error };
};

export const useUpdateClient = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const updateClient = async (id: ID, data: UpdateClientRequest, options?: { onSuccess?: (item: Client) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await clientService.update(id, data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { updateClient, loading, error };
};

export const useDeleteClient = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const deleteClient = async (id: ID, options?: { onSuccess?: () => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { await clientService.delete(id); options?.onSuccess?.(); }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { deleteClient, loading, error };
};
